<script setup lang="ts">
import { computed, inject } from 'vue';
import SidePeekButton from '@/components/SidePeekButton.vue';
import { SOURCE_TYPES } from '@/stores/trackMatchingStore';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useLazyOverflowTooltip } from '@/composables/useLazyOverflowTooltip';
import { useOpenRecordInInspector } from '@/composables/useOpenRecordInInspector';
import { adminAppEntityFor, adminAppRecordUrl } from '@/utils/adminAppLink';
import { entityDisplayNameFor } from '@/composables/entityColumns';
import { ROW_OPENS_IN_PEEK } from '@/components/DataTable/rowClick';

const props = defineProps<{
  field: string;
  dataType: string;
  data: any;
  foreignModelType?: string;
}>();

const adminAppEntity = computed(() => {
  // If foreignModelType is provided, use it (for foreign key ID columns).
  // Through the SAME derivation as the row's own id, not verbatim: a foreign
  // key can point at a LiveTrack (remix_source_track_id does), and gvadmin has
  // no GVLiveTrack — it is GVTrack. Used raw, that column linked somewhere that
  // opens nothing, and disagreed with the id link in the exported spreadsheet.
  if (props.foreignModelType) {
    return adminAppEntityFor(props.foreignModelType);
  }
  // Otherwise, derive from data.model_type (for regular id columns)
  if (props.data.model_type) {
    return adminAppEntityFor(props.data.model_type);
  }
  return '';
});

/**
 * models.json key for the side-peek inspector. Unlike {@link adminAppEntity},
 * this keeps the real model basename — gvadmin strips the "Live" prefix
 * (LiveTrack is "GVTrack" over there), but models.json knows "LiveTrack".
 */
const inspectorEntityId = computed(() => {
  if (props.foreignModelType) {
    return props.foreignModelType;
  }
  if (props.data.model_type) {
    const segments = props.data.model_type.split('\\');
    return segments[segments.length - 1].trim();
  }
  return '';
});

const displayValue = computed(() => props.data[props.field]);

const isSpotifyLink = computed(() => props.data.source === SOURCE_TYPES.SPOTIFY);

/**
 * Same `gvadmin://goto/…` shape the inspector's ID label links to. Null for a
 * model the Admin app cannot open, such as a hardware unit: the value then
 * shows as plain text rather than as a link that does nothing.
 */
const adminAppHref = computed<string | null>(() => {
  if (isSpotifyLink.value) {
    return `spotify://track/${displayValue.value}`;
  }
  return adminAppEntity.value === '' ? null : adminAppRecordUrl(adminAppEntity.value, displayValue.value);
});

/** The record's name in the interface, for example "Unit" for a hardware Player. */
const sidePeekNoun = computed(() => entityDisplayNameFor(inspectorEntityId.value));

/** Only offer the side peek when models.json knows the entity (columns + slug derivable). */
const canOpenInSidePeek = computed(
  () =>
    !isSpotifyLink.value &&
    displayValue.value != null &&
    canOpen(inspectorEntityId.value)
);

/**
 * The row's own id, not a foreign key.
 * In a table where clicking the row already opens this same record, this cell's button would open it again, so it is hidden.
 * A foreign key opens a different record, so it keeps its button.
 */
const rowOpensInPeek = inject(ROW_OPENS_IN_PEEK, () => false);
const showSidePeekButton = computed(
  () =>
    canOpenInSidePeek.value &&
    !(!props.foreignModelType && props.field === 'id' && rowOpensInPeek(props.data))
);

const { canOpen } = useOpenRecordInInspector();

// Lazy overflow tooltip for the id/FK value. The id column renders for every row (and every
// track-match row), so eagerly mounting a tooltip per cell froze the page on load. Uses the shared
// useLazyOverflowTooltip composable rather than the OverflowTooltip *component* — the trigger here
// is wrapped in an <a>, and this file has an import cycle via ModelRecordInspector that would make
// a component import fail to resolve; a composable has neither problem.
const {
  activated,
  probeRef,
  onProbePointerOver,
  onProbePointerOut,
  onProbeFocusIn,
  onProbeFocusOut,
  textRef,
  tooltipOpen,
  onOpenChange,
} = useLazyOverflowTooltip(displayValue);

</script>

<template>
  <span class="group/recordlink relative flex min-w-0 max-w-full items-center gap-1">
    <!-- The link is only as wide as the id. The rest of the cell is plain space, so a click there is a row click. -->
    <component
      :is="adminAppHref === null ? 'span' : 'a'"
      :href="adminAppHref ?? undefined"
      class="block min-w-0"
      :class="adminAppHref !== null && 'underline-offset-2 hover:underline'"
    >
      <span
        v-if="!activated"
        ref="probeRef"
        tabindex="-1"
        class="block w-full min-w-0 max-w-full truncate"
        @pointerover="onProbePointerOver"
        @pointerout="onProbePointerOut"
        @focusin="onProbeFocusIn"
        @focusout="onProbeFocusOut"
      >{{ displayValue }}</span>
      <Tooltip v-else :open="tooltipOpen" @update:open="onOpenChange">
        <TooltipTrigger as-child>
          <span
            ref="textRef"
            tabindex="-1"
            class="block w-full min-w-0 max-w-full truncate"
          >
            {{ displayValue }}
          </span>
        </TooltipTrigger>
        <TooltipContent class="pointer-events-none">
          <p class="pointer-events-none">{{ displayValue }}</p>
        </TooltipContent>
      </Tooltip>
    </component>
    <SidePeekButton
      v-if="showSidePeekButton"
      :entity-id="inspectorEntityId"
      :record-id="displayValue"
      :label="`${sidePeekNoun} ${displayValue}`"
    />
  </span>
</template>
